import moment from 'moment-timezone';
import { Action, ActionReducer, createReducer, on } from '@ngrx/store';

import { ROLES_KEYS, SupportChat, SupportChatInList } from '@interfaces';
import { SupportChatState, initialState } from './support-chat.state';
import * as actions from './support-chat.actions';

const supportChatToSupportChatInList = (supportChat: SupportChat): SupportChatInList => {
  const { lastMessageAt, readByEmployeeAt } = structuredClone(supportChat);
  let hasUnreadMessages = false;

  if (!readByEmployeeAt && lastMessageAt) {
    hasUnreadMessages = true;
  }

  if (readByEmployeeAt && lastMessageAt) {
    hasUnreadMessages = moment(lastMessageAt).isAfter(moment(readByEmployeeAt));
  }

  return {
    ...supportChat,
    hasUnreadMessages,
  };
};

const reducer: ActionReducer<SupportChatState, Action> = createReducer(
  initialState,
  on(
    actions.searchSupportChats,
    (state, { opts, inBackground }): SupportChatState => ({
      ...state,
      pagination: { ...state.pagination, ...opts },
      loadingSupportChats: !inBackground,
      errorMessage: null,
    })
  ),
  on(actions.searchSupportChatsSuccess, (state, { supportChats: { data, pagination } }): SupportChatState => {
    const foreigners: { [key: string]: SupportChatInList } = {};
    const employers: { [key: string]: SupportChatInList } = {};

    data.forEach(chat => {
      const mapped = supportChatToSupportChatInList(chat);

      if (chat.supportedUser.role.key === ROLES_KEYS.Foreigner) {
        foreigners[chat.id] = mapped;
        return;
      }

      employers[chat.id] = mapped;
    });

    return {
      ...state,
      pagination: { ...state.pagination, ...(pagination as any) },
      foreignersSupportChats: foreigners,
      employersSupportChats: employers,
      loadingSupportChats: false,
      errorMessage: null,
    };
  }),
  on(
    actions.searchSupportChatsError,
    (state, { errorMessage }): SupportChatState => ({
      ...state,
      loadingSupportChats: false,
      errorMessage,
    })
  ),

  on(
    actions.loadMoreSupportChats,
    (state, { opts }): SupportChatState => ({
      ...state,
      pagination: { ...state.pagination, ...opts },
      loadingMoreSupportChats: true,
      errorMessage: null,
    })
  ),
  on(actions.loadMoreSupportChatsSuccess, (state, { supportChats: { data, pagination } }): SupportChatState => {
    const foreigners = { ...state.foreignersSupportChats };
    const employers = { ...state.employersSupportChats };

    data.forEach(chat => {
      const roleKey = chat.supportedUser.role.key;

      if (roleKey === ROLES_KEYS.Foreigner) {
        foreigners[chat.id] = supportChatToSupportChatInList(chat);
        return;
      }

      employers[chat.id] = supportChatToSupportChatInList(chat);
    });

    return {
      ...state,
      pagination: { ...state.pagination, ...(pagination as any) },
      foreignersSupportChats: foreigners,
      employersSupportChats: employers,
      loadingMoreSupportChats: false,
      errorMessage: null,
    };
  }),
  on(
    actions.loadMoreSupportChatsError,
    (state, { errorMessage }): SupportChatState => ({
      ...state,
      loadingMoreSupportChats: false,
      errorMessage,
    })
  ),

  on(
    actions.addSupportChatMarker,
    (state, { opts }): SupportChatState => ({
      ...state,
      loadingSupportChatUpdate: opts.supportChatId,
      errorMessage: null,
    })
  ),
  on(actions.addSupportChatMarkerSuccess, (state, { supportChat }): SupportChatState => {
    const isForeigner = supportChat.supportedUser.role.key === ROLES_KEYS.Foreigner;

    const collectionKey = isForeigner ? 'foreignersSupportChats' : 'employersSupportChats';
    const currentCollection = state[collectionKey];

    return {
      ...state,
      [collectionKey]: {
        ...currentCollection,
        [supportChat.id]: supportChatToSupportChatInList(supportChat),
      },
      loadingSupportChatUpdate: null,
      errorMessage: null,
    };
  }),
  on(
    actions.addSupportChatMarkerError,
    (state, { errorMessage }): SupportChatState => ({
      ...state,
      loadingSupportChatUpdate: null,
      errorMessage,
    })
  ),

  on(
    actions.markSupportChatAsUnread,
    (state, { supportChatId }): SupportChatState => ({
      ...state,
      loadingSupportChatUpdate: supportChatId,
      errorMessage: null,
    })
  ),
  on(actions.markSupportChatAsUnreadSuccess, (state, { supportChat }): SupportChatState => {
    const isForeigner = supportChat.supportedUser.role.key === ROLES_KEYS.Foreigner;

    const collectionKey = isForeigner ? 'foreignersSupportChats' : 'employersSupportChats';
    const currentCollection = state[collectionKey];

    return {
      ...state,
      [collectionKey]: {
        ...currentCollection,
        [supportChat.id]: supportChatToSupportChatInList(supportChat),
      },
      loadingSupportChatUpdate: null,
      errorMessage: null,
    };
  }),
  on(
    actions.markSupportChatAsUnreadError,
    (state, { errorMessage }): SupportChatState => ({
      ...state,
      loadingSupportChatUpdate: null,
      errorMessage,
    })
  ),

  on(
    actions.markSupportChatAsRead,
    (state, { supportChatId }): SupportChatState => ({
      ...state,
      loadingSupportChatUpdate: supportChatId,
      errorMessage: null,
    })
  ),
  on(actions.markSupportChatAsReadSuccess, (state, { supportChat }): SupportChatState => {
    const isForeigner = supportChat.supportedUser.role.key === ROLES_KEYS.Foreigner;

    const collectionKey = isForeigner ? 'foreignersSupportChats' : 'employersSupportChats';
    const currentCollection = state[collectionKey];

    return {
      ...state,
      [collectionKey]: {
        ...currentCollection,
        [supportChat.id]: supportChatToSupportChatInList(supportChat),
      },
      loadingSupportChatUpdate: null,
      errorMessage: null,
    };
  }),
  on(
    actions.markSupportChatAsReadError,
    (state, { errorMessage }): SupportChatState => ({
      ...state,
      loadingSupportChatUpdate: null,
      errorMessage,
    })
  ),

  on(
    actions.openSupportChat,
    (state, { supportChatId }): SupportChatState => ({
      ...state,
      loadingOpenSupportChat: true,
      loadingSupportChatUpdate: supportChatId,
      errorMessage: null,
    })
  ),
  on(actions.openSupportChatSuccess, (state, { messages, supportChat }): SupportChatState => {
    const isForeigner = supportChat.supportedUser.role.key === ROLES_KEYS.Foreigner;

    const collectionKey = isForeigner ? 'foreignersSupportChats' : 'employersSupportChats';
    const currentCollection = state[collectionKey];

    const existingChat = currentCollection[supportChat.id];

    const mapped = supportChatToSupportChatInList(supportChat);

    const mappedWithOnline = {
      ...mapped,
      supportedUserOnline: existingChat?.supportedUserOnline ?? false,
    };

    return {
      ...state,
      [collectionKey]: existingChat
        ? {
            ...currentCollection,
            [supportChat.id]: mappedWithOnline,
          }
        : currentCollection,
      openSupportChat: {
        isMinimized: false,
        unreadMessagesCount: 0,
        messageAttachmentsToAdd: {},
        messages: structuredClone(messages),
        supportChat: {
          ...supportChat,
          supportedUserOnline: existingChat?.supportedUserOnline ?? false,
        },
      },
      loadingOpenSupportChat: false,
      loadingSupportChatUpdate: null,
      errorMessage: null,
    };
  }),
  on(
    actions.openSupportChatError,
    (state, { errorMessage }): SupportChatState => ({
      ...state,
      loadingOpenSupportChat: false,
      loadingSupportChatUpdate: null,
      errorMessage,
    })
  ),

  on(
    actions.closeSupportChat,
    (state, { supportChatId }): SupportChatState => ({
      ...state,
      loadingOpenSupportChat: true,
      loadingSupportChatUpdate: supportChatId,
      errorMessage: null,
    })
  ),
  on(
    actions.closeSupportChatSuccess,
    (state): SupportChatState => ({
      ...state,
      openSupportChat: null,
      loadingOpenSupportChat: false,
      loadingSupportChatUpdate: null,
      errorMessage: null,
    })
  ),
  on(
    actions.closeSupportChatError,
    (state, { errorMessage }): SupportChatState => ({
      ...state,
      loadingOpenSupportChat: false,
      loadingSupportChatUpdate: null,
      errorMessage,
    })
  ),

  on(
    actions.createSupportMessage,
    (state): SupportChatState => ({
      ...state,
      sendingMessage: true,
      errorMessage: null,
    })
  ),

  on(actions.createSupportMessageSuccess, (state, { supportMessage }): SupportChatState => {
    if (state.openSupportChat?.supportChat?.id === supportMessage.supportChatId) {
      return {
        ...state,
        openSupportChat: {
          ...state.openSupportChat,
          unreadMessagesCount: state.openSupportChat.isMinimized
            ? state.openSupportChat.unreadMessagesCount + 1
            : state.openSupportChat.unreadMessagesCount,
          messageAttachmentsToAdd: {},
          messages: [...state.openSupportChat.messages, structuredClone(supportMessage)],
        },
        sendingMessage: false,
        errorMessage: null,
      };
    } else {
      return {
        ...state,
        sendingMessage: false,
        errorMessage: null,
      };
    }
  }),

  on(
    actions.editSupportMessage,
    (state): SupportChatState => ({
      ...state,
      loadingOpenSupportChat: true,
      errorMessage: null,
    })
  ),
  on(actions.editSupportMessageSuccess, (state, { supportMessage }): SupportChatState => {
    return {
      ...state,
      openSupportChat: {
        ...state.openSupportChat,
        messageAttachmentsToAdd: {},
        messages: state.openSupportChat.messages.map(msg =>
          msg.id === supportMessage.id ? { ...supportMessage } : { ...msg }
        ),
      },
      loadingOpenSupportChat: false,
      errorMessage: null,
    };
  }),
  on(
    actions.editSupportMessageError,
    (state, { errorMessage }): SupportChatState => ({
      ...state,
      loadingOpenSupportChat: false,
      errorMessage,
    })
  ),

  on(
    actions.removeSupportMessage,
    (state): SupportChatState => ({
      ...state,
      loadingOpenSupportChat: true,
      errorMessage: null,
    })
  ),
  on(actions.removeSupportMessageSuccess, (state, { removedSupportMessageId }): SupportChatState => {
    return {
      ...state,
      openSupportChat: {
        ...state.openSupportChat,
        messages: state.openSupportChat.messages.filter(msg => msg.id !== removedSupportMessageId),
      },
      loadingOpenSupportChat: false,
      errorMessage: null,
    };
  }),
  on(
    actions.removeSupportMessageError,
    (state, { errorMessage }): SupportChatState => ({
      ...state,
      loadingOpenSupportChat: false,
      errorMessage,
    })
  ),
  on(actions.updateSupportChat, (state, { supportChat }): SupportChatState => {
    const isForeigner = supportChat.supportedUser.role.key === ROLES_KEYS.Foreigner;

    const collectionKey = isForeigner ? 'foreignersSupportChats' : 'employersSupportChats';
    const currentCollection = state[collectionKey];

    const existOnTheList = currentCollection[supportChat.id];

    if (!existOnTheList) {
      return state;
    }

    const mapped = supportChatToSupportChatInList(supportChat);

    const mappedWithOnline =
      existOnTheList.supportedUserOnline !== undefined
        ? {
            ...mapped,
            supportedUserOnline: existOnTheList.supportedUserOnline,
          }
        : mapped;

    return {
      ...state,
      [collectionKey]: {
        ...currentCollection,
        [supportChat.id]: mappedWithOnline,
      },
    };
  }),

  on(actions.updateSupportChatAndPosition, (state, { supportChat }): SupportChatState => {
    const isForeigner = supportChat.supportedUser.role.key === ROLES_KEYS.Foreigner;

    const targetKey = isForeigner ? 'foreignersSupportChats' : 'employersSupportChats';
    const otherKey = isForeigner ? 'employersSupportChats' : 'foreignersSupportChats';

    const targetCollection = { ...state[targetKey] };
    const otherCollection = { ...state[otherKey] };

    const existing = targetCollection[supportChat.id] ?? otherCollection[supportChat.id];

    delete targetCollection[supportChat.id];
    delete otherCollection[supportChat.id];

    const mapped = supportChatToSupportChatInList(supportChat);

    const mappedWithOnline =
      existing?.supportedUserOnline !== undefined
        ? {
            ...mapped,
            supportedUserOnline: existing.supportedUserOnline,
          }
        : mapped;

    return {
      ...state,
      [targetKey]: {
        [supportChat.id]: mappedWithOnline,
        ...targetCollection,
      },
      [otherKey]: otherCollection,
    };
  }),

  on(actions.uploadAttachments, (state, { opts: { files } }): SupportChatState => {
    const filesAsObj = {} as any;
    files.forEach(file => {
      filesAsObj[file.fileOriginalName] = {
        error: null,
        loading: true,
        uploaded: false,
        userAsset: file,
      };
    });

    return {
      ...state,
      loadingOpenSupportChat: true,
      openSupportChat: {
        ...state.openSupportChat,
        messageAttachmentsToAdd: {
          ...state.openSupportChat.messageAttachmentsToAdd,
          ...filesAsObj,
        },
      },
    };
  }),
  on(actions.uploadAttachmentsSuccess, (state, { opts: { uploadedFiles } }): SupportChatState => {
    const inStateObj = {} as any;
    Object.entries(uploadedFiles).forEach(([key, fileUploadResult]) => {
      inStateObj[key] = {
        uploaded: fileUploadResult.uploaded,
        loading: false,
        error: fileUploadResult.errorMessage || null,
        userAsset: fileUploadResult.userAsset || null,
      };
    });
    return {
      ...state,
      loadingOpenSupportChat: false,
      openSupportChat: {
        ...state.openSupportChat,
        messageAttachmentsToAdd: {
          ...state.openSupportChat.messageAttachmentsToAdd,
          ...inStateObj,
        },
      },
    };
  }),
  on(actions.uploadAttachmentsError, (state, { opts: { errorMessage } }): SupportChatState => {
    return {
      ...state,
      errorMessage,
      loadingOpenSupportChat: false,
    };
  }),

  on(actions.removeAttachmentFromUploading, (state, { opts: { fileOriginalName } }): SupportChatState => {
    return {
      ...state,
      openSupportChat: {
        ...state.openSupportChat,
        messageAttachmentsToAdd: {
          ...state.openSupportChat.messageAttachmentsToAdd,
          [fileOriginalName]: {
            ...state.openSupportChat.messageAttachmentsToAdd[fileOriginalName],
            loading: true,
            uploaded: false,
          },
        },
      },
    };
  }),
  on(actions.removeAttachmentFromUploadingSuccess, (state, { opts: { fileOriginalName } }): SupportChatState => {
    const rItems = { ...state.openSupportChat.messageAttachmentsToAdd };
    delete rItems[fileOriginalName];
    return {
      ...state,
      openSupportChat: {
        ...state.openSupportChat,
        messageAttachmentsToAdd: { ...rItems },
      },
    };
  }),
  on(actions.removeAttachmentFromUploadingError, (state, { opts: { fileOriginalName } }): SupportChatState => {
    return {
      ...state,
      openSupportChat: {
        ...state.openSupportChat,
        messageAttachmentsToAdd: {
          ...state.openSupportChat.messageAttachmentsToAdd,
          [fileOriginalName]: {
            ...state.openSupportChat.messageAttachmentsToAdd[fileOriginalName],
            error: 'ERROR_REMOVING',
          },
        },
      },
    };
  }),

  on(actions.removeExistingAttachment, (state, {}): SupportChatState => {
    return {
      ...state,
      loadingOpenSupportChat: true,
    };
  }),
  on(actions.removeExistingAttachmentSuccess, (state): SupportChatState => {
    return {
      ...state,
      loadingOpenSupportChat: false,
    };
  }),
  on(actions.removeExistingAttachmentError, (state, { opts: { errorMessage } }): SupportChatState => {
    return {
      ...state,
      loadingOpenSupportChat: false,
      errorMessage,
    };
  }),

  on(actions.getTranscription, (state, {}): SupportChatState => {
    return {
      ...state,
      loadingTranscriptionSupportChat: true,
    };
  }),
  on(actions.getTranscriptionSuccess, (state): SupportChatState => {
    return {
      ...state,
      loadingTranscriptionSupportChat: false,
    };
  }),
  on(actions.getTranscriptionError, (state, { opts: { errorMessage } }): SupportChatState => {
    return {
      ...state,
      loadingTranscriptionSupportChat: false,
      errorMessage,
    };
  }),

  on(actions.getMyUnreadCountSuccess, (state, { opts }): SupportChatState => {
    return {
      ...state,
      unreadCount: opts?.count,
      unreadCountForeigners: opts?.foreigners,
      unreadCountEmployers: opts?.employers,
    };
  }),

  on(actions.setForeignerOnlineStatus, (state, { opts }): SupportChatState => {
    const updatedSupportChats = Object.entries(state.foreignersSupportChats).reduce<
      typeof state.foreignersSupportChats
    >((acc, [chatId, chat]) => {
      if (chat.supportedUserId !== opts.foreignerId) {
        acc[chatId] = chat;
        return acc;
      }

      acc[chatId] = {
        ...chat,
        supportedUserOnline: opts.status,
      };

      return acc;
    }, {});

    const openSupportChat = state.openSupportChat?.supportChat;

    const updatedOpenSupportChat =
      openSupportChat && openSupportChat.supportedUserId === opts.foreignerId
        ? {
            ...state.openSupportChat,
            supportChat: {
              ...openSupportChat,
              supportedUserOnline: opts.status,
            },
          }
        : state.openSupportChat;

    return {
      ...state,
      foreignersSupportChats: updatedSupportChats,
      openSupportChat: updatedOpenSupportChat,
    };
  }),

  on(actions.setEmployerOnlineStatus, (state, { opts }): SupportChatState => {
    const updatedSupportChats = Object.entries(state.employersSupportChats).reduce<typeof state.employersSupportChats>(
      (acc, [chatId, chat]) => {
        if (chat.supportedUserId !== opts.employerId) {
          acc[chatId] = chat;
          return acc;
        }

        acc[chatId] = {
          ...chat,
          supportedUserOnline: opts.status,
        };

        return acc;
      },
      {}
    );

    const openSupportChat = state.openSupportChat?.supportChat;

    const updatedOpenSupportChat =
      openSupportChat && openSupportChat.supportedUserId === opts.employerId
        ? {
            ...state.openSupportChat,
            supportChat: {
              ...openSupportChat,
              supportedUserOnline: opts.status,
            },
          }
        : state.openSupportChat;

    return {
      ...state,
      employersSupportChats: updatedSupportChats,
      openSupportChat: updatedOpenSupportChat,
    };
  }),

  on(actions.setMinimizedStatus, (state, { isMinimized }): SupportChatState => {
    return {
      ...state,
      openSupportChat: {
        ...state.openSupportChat,
        isMinimized,
        unreadMessagesCount: isMinimized ? state.openSupportChat.unreadMessagesCount : 0,
      },
    };
  }),

  // this actions refetches all support chats with current filter options
  // and opens created support chat
  // @TODO user should not be able to create support chats because +
  // + system creates support chat automatically for foreigner that activates account
  // + system cares that everey activated foreigner has access to support-chat (has its own support chat)
  on(
    actions.createSupportChat,
    (state, {}): SupportChatState => ({
      ...state,
      loadingSupportChats: true,
    })
  )
);

export function supportChatReducer(state: SupportChatState, action: Action): SupportChatState {
  return reducer(state, action);
}
